import { useMemo } from "react";
import { isDesignChangeOn } from "@/app/design-review/useDesignChange";
import { StatusPage, statusActionCn } from "@/app/ui/StatusPage";
import { useLocation, useNavigate, useParams as useRouteParams } from "react-router-dom";

export { Navigate } from "react-router-dom";

export function usePathname() {
  return useLocation().pathname;
}

export function useSearchParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// Next-compatible generic typing: callers declare the resolved params shape.
export function useParams<T = Record<string, string | undefined>>(): T {
  return useRouteParams() as T;
}

// Keep the imperative navigation API used across the app.
export function useRouter() {
  const navigate = useNavigate();
  return useMemo(
    () => ({
      push: (href: string, options?: { scroll?: boolean }) =>
        navigate(href, { preventScrollReset: options?.scroll === false }),
      replace: (href: string, options?: { scroll?: boolean }) =>
        navigate(href, { replace: true, preventScrollReset: options?.scroll === false }),
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => window.location.reload(),
    }),
    [navigate],
  );
}

export function NotFound() {
  if (isDesignChangeOn("not-found")) {
    return (
      <StatusPage
        code="404"
        title="This page drifted into the void"
        message="The link may be broken or the page may have moved. Head back to your dashboard to keep exploring."
        action={
          <a href="/login" className={statusActionCn}>
            Back to Voidpulse
          </a>
        }
      />
    );
  }
  return (
    <main className="flex min-h-screen items-center justify-center">
      <h1>404 - Page not found</h1>
    </main>
  );
}
